import { BrandLockup } from "@/components/brand/BrandLockup";
import { WalletBalanceMenu } from "@/components/layout/WalletBalanceMenu";
import { ThemeToggle } from "@/components/layout/ThemeToggle";
import { Button } from "@/components/ui/Button";
import { useAuth } from "@/lib/auth";
import { ROUTES } from "@/lib/routes";
import { shortPrincipal } from "@/lib/utils";
import { LogOut, Search, UserRound, Wallet } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Utilities chrome only — product nav lives in PrimaryNavRow.
 * Brand · Search · Portfolio≈USDM · Wallet · Profile · Theme · Logout
 */
export function AppTopBar({ onOpenCommand }: { onOpenCommand: () => void }) {
	const {
		isAuthenticated,
		isLoggingIn,
		principal,
		login,
		logout,
	} = useAuth();

	return (
		<header className="border-b border-border/60 bg-card/95 backdrop-blur-xl">
			<div className="container mx-auto flex h-14 items-center gap-2 px-3 sm:px-4">
				<div className="min-w-0 shrink-0">
					<BrandLockup compact />
				</div>

				<div className="ml-auto flex shrink-0 items-center gap-1 sm:gap-1.5">
					<button
						type="button"
						onClick={onOpenCommand}
						className="inline-flex h-10 min-w-10 items-center justify-center gap-1.5 rounded-xl border border-border bg-card px-2 text-sm font-semibold hover:border-primary/40 sm:px-3"
						aria-label="Search"
					>
						<Search className="size-4 text-primary" />
						<span className="hidden xl:inline">Search</span>
					</button>

					<WalletBalanceMenu />

					{isAuthenticated ? (
						<Link
							to={ROUTES.wallet}
							className="inline-flex h-10 items-center justify-center gap-1.5 rounded-xl border border-border bg-card px-2.5 text-sm font-semibold hover:border-primary/40 sm:px-3"
							aria-label="Wallet"
							title="Wallet"
						>
							<Wallet className="size-4 shrink-0 text-primary" />
							<span>Wallet</span>
						</Link>
					) : null}

					{isAuthenticated && principal ? (
						<Link
							to={ROUTES.profile}
							className="inline-flex h-10 max-w-[9rem] items-center gap-1.5 rounded-xl border border-border bg-card px-2 hover:border-primary/40 sm:max-w-none sm:px-2.5"
							title={principal}
						>
							<UserRound className="size-4 shrink-0" />
							<span className="hidden truncate font-mono text-[11px] font-semibold sm:inline">
								{shortPrincipal(principal)}
							</span>
						</Link>
					) : (
						<Link
							to={ROUTES.profile}
							className="inline-flex h-10 min-w-10 items-center justify-center rounded-xl border border-border bg-card hover:border-primary/40"
							aria-label="Profile"
						>
							<UserRound className="size-4" />
						</Link>
					)}

					<ThemeToggle />

					{isAuthenticated ? (
						<Button
							variant="outline"
							size="sm"
							className="!min-h-10 gap-1.5 px-2 sm:px-3"
							onClick={() => void logout()}
							aria-label="Log out"
						>
							<LogOut className="size-4" />
							<span className="hidden sm:inline">Log out</span>
						</Button>
					) : (
						<Button
							variant="primary"
							size="sm"
							className="!min-h-10 !bg-primary !text-primary-foreground hover:!bg-primary-hover"
							loading={isLoggingIn}
							onClick={() => void login()}
						>
							Connect
						</Button>
					)}
				</div>
			</div>
		</header>
	);
}
